import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AddUser from "../components/AddUser";
import UpdateUser from "../components/UpdateUser";
import UpdatePasswordModal from "../components/UpdatePasswordModal"
import AuthContext from "../AuthContext";
import { privateAxios } from "../utils/axios";
import usePermissions from "../hooks/usePermissions";
import { usePermissionContext } from "../contexts/PermissionContext";
import toast from "react-hot-toast";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

function UserManagement() {
  const [showUserModal, setShowUserModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateUser, setUpdateUser] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const limitOptions = [5, 10, 20, 50, 100];
  const searchInputRef = useRef(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false); // ✅ state
  const [selectedUserId, setSelectedUserId] = useState(null); // ✅ store userId


  const authContext = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { userData } = usePermissions();
  const { refreshUserPermissions } = usePermissionContext();

  // Debounce the search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Fetch users with React Query
  const {
    data: usersData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "users",
      currentPage,
      debouncedSearchTerm,
      limit,
      roleFilter,
      statusFilter,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        page: currentPage.toString(),
        search: debouncedSearchTerm,
      });

      if (roleFilter !== "all") {
        params.append("role", roleFilter);
      }

      if (statusFilter !== "all") {
        params.append("isActive", statusFilter === "active" ? "true" : "false");
      }

      const response = await privateAxios.get(`/users/?${params.toString()}`);
      if (!response.data.success) {
        throw new Error("Network response was not ok");
      }
      return response.data;
    },
  });

  // Keep focus on search input after data fetch
  useEffect(() => {
    if (
      searchInputRef.current &&
      document.activeElement !== searchInputRef.current
    ) {
      searchInputRef.current.focus();
    }
  }, [usersData]);

  // Modal for User ADD
  const addUserModalSetting = () => {
    setShowUserModal(!showUserModal);
  };

  // Modal for User UPDATE
  const updateUserModalSetting = (selectedUserData) => {
    setUpdateUser(selectedUserData);
    setShowUpdateModal(!showUpdateModal);
  };

  const passwordUpdateModalSetting = (id) => {
    setSelectedUserId(id);
    setShowPasswordModal(!showPasswordModal);
  };

  // Delete user
const deleteUser = async (id) => {
  const confirmed = window.confirm(
    "Are you sure you want to delete this user? This action cannot be undone."
  );

  if (!confirmed) return;

  try {
    const response = await privateAxios.delete(`/users/${id}`);
    if (response.data.success) {
      toast.success("User deleted successfully");
      queryClient.invalidateQueries(["users"]);
    } else {
      toast.error(response.data.message || "Failed to delete user");
    }
  } catch (err) {
    console.error("Error deleting user:", err);
    toast.error(err.response?.data?.message || "Error deleting user");
  }
};
  // Toggle user status
  const toggleUserStatus = async (id, currentStatus) => {
    try {
      const response = await privateAxios.patch(`/users/${id}/status`, {
        isActive: !currentStatus,
      });
      if (response.data.success) {
        toast.success(
          `User ${!currentStatus ? "activated" : "deactivated"} successfully`
        );
        queryClient.invalidateQueries(["users"]);
      } else {
        toast.error(response.data.message || "Failed to update user status");
      }
    } catch (err) {
      console.error("Error updating user status:", err);
      toast.error(err.response?.data?.message || "Error updating user status");
    }
  };

  // Handle Search Term with debounce
  const handleSearchTerm = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  // Handle Page Change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // When limit changes, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [limit, roleFilter, statusFilter]);

  // Handle page update with permission refresh
  const handlePageUpdate = async () => {
    try {
      // Refresh the user list
      queryClient.invalidateQueries(["users"]);

      // Check if the current user's permissions were updated
      const currentUserId = userData?._id;
      if (currentUserId) {
        // Refresh current user's permissions
        await refreshUserPermissions();
        // toast.success("User updated successfully");
      }
    } catch (error) {
      console.error("Error refreshing permissions:", error);
      // toast.success("User updated successfully");
    }
  };

  if (error) return (
    <div className="col-span-12 lg:col-span-10 flex justify-center items-center min-h-[300px]">
      <div className="flex flex-col items-center gap-3 text-red-500">
        <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
        <span className="text-sm font-medium text-gray-600">Error: {error.message}</span>
      </div>
    </div>
  );

  return (
    <div className="col-span-12 lg:col-span-10 flex justify-center bg-gray-50 min-h-screen">
      <div className="flex flex-col gap-6 w-11/12 max-w-screen-xl">

        {/* ── Page Header ── */}
        <div className="flex items-center justify-between mt-8 mb-2">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">User Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage accounts, roles, and permissions</p>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
          {/* Total Users */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 shrink-0">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Users</p>
              <p className="text-3xl font-bold text-gray-900 mt-0.5">{usersData?.data?.totalUsers || 0}</p>
            </div>
          </div>

          {/* Active Users */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100 shrink-0">
              <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Active Users</p>
              <p className="text-3xl font-bold text-gray-900 mt-0.5">
                {usersData?.data?.users?.filter((user) => user.isActive).length || 0}
              </p>
            </div>
          </div>

          {/* Admin Users */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-violet-50 to-violet-100 shrink-0">
              <svg className="w-6 h-6 text-violet-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" fill="none" />
              </svg>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Admin Users</p>
              <p className="text-3xl font-bold text-gray-900 mt-0.5">
                {usersData?.data?.users?.filter((user) => user.role === "admin").length || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showUserModal && (
          <AddUser
            addUserModalSetting={addUserModalSetting}
            handlePageUpdate={handlePageUpdate}
          />
        )}
        {showUpdateModal && (
          <UpdateUser
            updateUserData={updateUser}
            updateModalSetting={updateUserModalSetting}
            handlePageUpdate={handlePageUpdate}
          />
        )}
        {/* ✅ Show Password Update Modal */}
        {showPasswordModal && (
          <UpdatePasswordModal
            userId={selectedUserId}
            onClose={() => setShowPasswordModal(false)}
          />
        )}

        {/* ── Table Card ── */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm mb-10 overflow-hidden">

          {/* Toolbar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold text-gray-800">All Users</h2>
              {!isLoading && (
                <span className="text-xs font-medium bg-blue-50 text-blue-600 px-2.5 py-0.5 rounded-full border border-blue-100">
                  {usersData?.data?.totalUsers || 0} total
                </span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
              {/* Search */}
              <div className="relative flex items-center border border-gray-200 rounded-xl shadow-sm bg-white hover:border-blue-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all duration-150 px-3 py-2 w-full sm:w-72">
                <img
                  alt="search-icon"
                  className="w-4 h-4 opacity-40 shrink-0"
                  src={require("../assets/search-icon.png")}
                />
                <input
                  ref={searchInputRef}
                  className="border-none outline-none focus:outline-none focus:ring-0 text-sm pl-2 pr-6 bg-transparent w-full text-gray-700 placeholder-gray-400"
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={handleSearchTerm}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="absolute right-3 text-gray-300 hover:text-gray-500 focus:outline-none"
                    onClick={() => {
                      setSearchTerm("");
                      searchInputRef.current?.focus();
                    }}
                    tabIndex={-1}
                  >
                    <svg className="h-3.5 w-3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Role Filter */}
              <select
                className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all duration-150 cursor-pointer"
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>

              {/* Status Filter */}
              <select
                className="px-3 py-2 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all duration-150 cursor-pointer"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>

              {/* Add User */}
              <button
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold px-5 py-2 text-sm rounded-xl transition-all duration-150 shadow-sm hover:shadow whitespace-nowrap"
                onClick={addUserModalSetting}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add User
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="whitespace-nowrap px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[6%]">
                    #
                  </th>
                  <th className="whitespace-nowrap px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[18%]">
                    Name
                  </th>
                  <th className="whitespace-nowrap px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[14%]">
                    Phone
                  </th>
                  <th className="whitespace-nowrap px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[16%]">
                    Email
                  </th>
                  <th className="whitespace-nowrap px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[10%]">
                    Role
                  </th>
                  <th className="whitespace-nowrap px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[18%]">
                    Permissions
                  </th>
                  <th className="whitespace-nowrap px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[10%]">
                    Status
                  </th>
                  <th className="whitespace-nowrap px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-[18%]">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-50">
                {isLoading ? (
                  <tr>
                    <td colSpan="8" className="px-5 py-16 text-center">
                      <div className="flex flex-col justify-center items-center gap-3">
                        <div className="relative w-10 h-10">
                          <div className="absolute inset-0 rounded-full border-2 border-blue-100"></div>
                          <div className="absolute inset-0 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>
                        </div>
                        <span className="text-sm text-gray-400 font-medium">Loading users...</span>
                      </div>
                    </td>
                  </tr>
                ) : usersData?.data?.users?.length > 0 ? (
                  usersData.data.users.map((element, index) => (
                    <tr
                      key={element._id}
                      className="hover:bg-blue-50/40 transition-colors duration-100 group"
                    >
                      <td className="whitespace-nowrap px-5 py-4 text-gray-400 text-sm font-mono">
                        {(currentPage - 1) * limit + index + 1}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0 shadow-sm">
                            {element.name?.charAt(0)?.toUpperCase() || "U"}
                          </div>
                          <span className="font-medium text-gray-900 capitalize">{element.name}</span>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-gray-600 text-sm">
                        {element.phone}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-gray-600 text-sm">
                        {element.email || (
                          <span className="text-gray-300 italic">N/A</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 capitalize text-sm">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${
                            element.role === "admin"
                              ? "bg-violet-50 text-violet-700 border border-violet-100"
                              : "bg-blue-50 text-blue-700 border border-blue-100"
                          }`}
                        >
                          {element.role === "admin" ? (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 1l2.39 4.843 5.345.777-3.867 3.77.913 5.32L10 13.347 5.22 15.71l.913-5.32L2.265 6.62l5.345-.777L10 1z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          )}
                          {element.role}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-sm max-w-[180px]">
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(element.permissions) && element.permissions.length > 0 ? (
                            typeof element.permissions[0] === "string" ? (
                              // Old format: ["read", "write", "delete"]
                              element.permissions.map((permission, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                >
                                  {permission}
                                </span>
                              ))
                            ) : (
                              // New format: [{module: "products", permissions: ["read", "write"]}]
                              element.permissions.map((module, idx) => (
                                <div key={idx} className="flex flex-col gap-1">
                                  <span className="text-xs font-semibold text-gray-500 capitalize">
                                    {module.module.replace("-", " ")}:
                                  </span>
                                  <div className="flex flex-nowrap gap-1 overflow-x-auto whitespace-nowrap">
                                    {module.permissions.map((permission, permIdx) => (
                                      <span
                                        key={permIdx}
                                        className="px-1.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100"
                                      >
                                        {permission}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              ))
                            )
                          ) : (
                            <span className="text-xs text-gray-300 italic">No permissions</span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${
                            element.isActive
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
                              : "bg-red-50 text-red-600 border border-red-100"
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${element.isActive ? "bg-emerald-500" : "bg-red-400"}`}></span>
                          {element.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-sm">
                        <div className="flex items-center gap-1">
                          {/* Edit */}
                          <button
                            onClick={() => updateUserModalSetting(element)}
                            title="Edit user"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-100 hover:border-blue-200 transition-all duration-150"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Edit
                          </button>

                          {/* Update Password */}
                          <button
                            onClick={() => passwordUpdateModalSetting(element._id)} // ✅ new button
                            title="Update password"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-100 hover:border-indigo-200 transition-all duration-150"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                            </svg>
                            Password
                          </button>

                          {/* Activate / Deactivate */}
                          <button
                            onClick={() => toggleUserStatus(element._id, element.isActive)}
                            title={element.isActive ? "Deactivate user" : "Activate user"}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-all duration-150 ${
                              element.isActive
                                ? "text-amber-600 bg-amber-50 hover:bg-amber-100 border-amber-100 hover:border-amber-200"
                                : "text-emerald-600 bg-emerald-50 hover:bg-emerald-100 border-emerald-100 hover:border-emerald-200"
                            }`}
                          >
                            {element.isActive ? (
                              <>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                </svg>
                                Deactivate
                              </>
                            ) : (
                              <>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Activate
                              </>
                            )}
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() => deleteUser(element._id)}
                            title="Delete user"
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg border border-red-100 hover:border-red-200 transition-all duration-150"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-5 py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                          <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                          </svg>
                        </div>
                        <p className="text-sm font-medium text-gray-400">No users found</p>
                        <p className="text-xs text-gray-300">Try adjusting your search or filters</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
            <p className="text-xs text-gray-400">
              Showing{" "}
              <span className="font-semibold text-gray-600">
                {usersData?.data?.users?.length
                  ? (currentPage - 1) * limit + 1
                  : 0}
              </span>
              {" – "}
              <span className="font-semibold text-gray-600">
                {Math.min(currentPage * limit, usersData?.data?.totalUsers || 0)}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-600">{usersData?.data?.totalUsers || 0}</span> users
            </p>

            <div className="flex items-center gap-3">
              <select
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 transition-all duration-150 cursor-pointer"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                {limitOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt} / page
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isLoading}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  Prev
                </button>

                <span className="px-3 py-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-200 rounded-lg min-w-[90px] text-center">
                  {currentPage} / {usersData?.data?.totalPages || 1}
                </span>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === (usersData?.data?.totalPages || 1) || isLoading}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150"
                >
                  Next
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default UserManagement;
